import { BeforeMount, Editor, OnMount } from "@monaco-editor/react";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import type * as monacoType from "monaco-editor";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyMonacoPlatformTheme } from "modules/record/record-producer/components/record-producer-fields/code-editor/applyMonacoPlatformTheme";
import { configureCodeEditor } from "modules/record/record-producer/components/record-producer-fields/code-editor/configureCodeEditor";
import { resolveCodeEditorProfile } from "modules/record/record-producer/components/record-producer-fields/code-editor/resolveCodeEditorProfile";
import {
    CodeEditorProfile,
    CodeEditorProfileId,
    CodeEditorTableSchema,
} from "modules/record/record-producer/components/record-producer-fields/code-editor/types";
import { StudioContextResponse } from "modules/studio/services/studio-api";

type StudioCodeEditorProps = {
    table: SysTable | null;
    column: SysColumn | null;
    value: string;
    record: Record<string, unknown>;
    allTables: SysTable[];
    allColumns: SysColumn[];
    studioConfig?: StudioContextResponse["studio"];
    profileId?: CodeEditorProfileId;
    modelKey?: string;
    processVariables?: { key: string; type: string }[];
    readOnly?: boolean;
    onChange: (value: string) => void;
    onValidityChange?: (message: string | null) => void;
};

const languageFromSubtype = (subtype?: string | null) => {
    const normalizedSubtype = String(subtype || "").trim().toLocaleLowerCase();

    switch (normalizedSubtype) {
        case "js":
        case "javascript":
            return "javascript";
        case "ts":
        case "typescript":
            return "typescript";
        case "html":
        case "xml":
            return "html";
        case "json":
            return "json";
        case "css":
            return "css";
        default:
            return normalizedSubtype || "plaintext";
    }
};

const extensionFromLanguage = (language: string) => {
    switch (language) {
        case "javascript":
            return "js";
        case "typescript":
            return "ts";
        case "html":
            return "html";
        case "json":
            return "json";
        case "css":
            return "css";
        default:
            return "txt";
    }
};

const configureLanguageDiagnostics = (monaco: typeof monacoType, language: string) => {
    if (language === "javascript" || language === "typescript") {
        const defaults = language === "typescript"
            ? monaco.languages.typescript.typescriptDefaults
            : monaco.languages.typescript.javascriptDefaults;

        defaults.setCompilerOptions({
            ...defaults.getCompilerOptions(),
            allowNonTsExtensions: true,
            checkJs: language === "javascript",
            noLib: false,
        });

        defaults.setDiagnosticsOptions({
            ...defaults.getDiagnosticsOptions(),
            noSemanticValidation: false,
            noSyntaxValidation: false,
        });
    }

    if (language === "html") {
        monaco.languages.html.htmlDefaults.setModeConfiguration({
            ...monaco.languages.html.htmlDefaults.modeConfiguration,
            diagnostics: true,
        });
    }

    if (language === "css") {
        monaco.languages.css.cssDefaults.setOptions({
            ...monaco.languages.css.cssDefaults.options,
            validate: true,
        });
    }
};

const tableSchemasFromMetadata = (
    allTables: SysTable[],
    allColumns: SysColumn[]
): CodeEditorTableSchema[] =>
    allTables.map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        label: candidate.label,
        columns: allColumns
            .filter((column) => column.sys_table_id === candidate.id)
            .sort((first, second) => first.order - second.order),
    }));

const metadataProfileMatches = (
    rule: Record<string, any>,
    table: SysTable,
    column: SysColumn,
    record: Record<string, unknown>
) => {
    if (rule.active === false || rule.active === "false") {
        return false;
    }

    if (rule.table_name !== table.name || rule.column_name !== column.name) {
        return false;
    }

    if (!rule.condition_ast) {
        return true;
    }

    try {
        const conditionAst = typeof rule.condition_ast === "string"
            ? JSON.parse(rule.condition_ast)
            : rule.condition_ast;

        if (!conditionAst?.field || !conditionAst?.operator) {
            return true;
        }

        const actual = record[conditionAst.field];

        if (conditionAst.operator === "eq") {
            return actual === conditionAst.value;
        }

        if (conditionAst.operator === "neq") {
            return actual !== conditionAst.value;
        }
    } catch (_error) {
        return true;
    }

    return true;
};

const buildMetadataProfile = ({
    table,
    column,
    record,
    allTables,
    allColumns,
    studioConfig,
}: {
    table: SysTable;
    column: SysColumn;
    record: Record<string, unknown>;
    allTables: SysTable[];
    allColumns: SysColumn[];
    studioConfig?: StudioContextResponse["studio"];
}): CodeEditorProfile | null => {
    if (column.column_type !== "CODE" || column.column_subtype !== "JAVASCRIPT" || !studioConfig) {
        return null;
    }

    const matchingRule = [...(studioConfig.code_editor_profile_rules || [])]
        .sort((first, second) => Number(first.order || 0) - Number(second.order || 0))
        .find((rule) => metadataProfileMatches(rule, table, column, record));

    const profileId = matchingRule?.profile_id;
    const profileRecord = (studioConfig.code_editor_profiles || [])
        .find((candidate) => candidate.id === profileId || candidate.name === profileId);

    if (!profileRecord) {
        return null;
    }

    const snippets = (studioConfig.code_editor_snippets || [])
        .filter((snippet) => snippet.active !== false && snippet.profile_id === profileRecord.id)
        .sort((first, second) => Number(first.order || 0) - Number(second.order || 0))
        .map((snippet) => ({
            label: snippet.label || "snippet",
            detail: snippet.detail || "",
            documentation: snippet.documentation || "",
            insertText: snippet.insert_text || "",
        }));

    const libs = (studioConfig.code_editor_libs || [])
        .filter((lib) => lib.active !== false && lib.profile_id === profileRecord.id)
        .sort((first, second) => Number(first.order || 0) - Number(second.order || 0))
        .map((lib) => lib.lib_code || "")
        .filter(Boolean);

    return {
        id: profileRecord.name as CodeEditorProfileId,
        language: "javascript",
        label: profileRecord.label || profileRecord.name,
        snippets,
        extraLib: [profileRecord.extra_lib_code, ...libs].filter(Boolean).join("\n\n"),
        targetTable: null,
        targetColumns: [],
        tableSchemas: tableSchemasFromMetadata(allTables, allColumns),
    };
};

const recordFingerprintForProfile = (
    record: Record<string, unknown>,
    column: SysColumn | null
) => {
    const nextRecord = { ...record };

    if (column?.name) {
        delete nextRecord[column.name];
    }

    return JSON.stringify(nextRecord);
};

const mergeProfiles = (
    metadataProfile: CodeEditorProfile | null,
    builtInProfile: CodeEditorProfile | null
) => {
    if (!metadataProfile) {
        return builtInProfile;
    }

    if (!builtInProfile) {
        return metadataProfile;
    }

    return {
        ...builtInProfile,
        label: metadataProfile.label || builtInProfile.label,
        snippets: metadataProfile.snippets.length > 0
            ? metadataProfile.snippets
            : builtInProfile.snippets,
        extraLib: [builtInProfile.extraLib, metadataProfile.extraLib].filter(Boolean).join("\n\n"),
        tableSchemas: metadataProfile.tableSchemas?.length
            ? metadataProfile.tableSchemas
            : builtInProfile.tableSchemas,
    };
};

const StudioCodeEditor = ({
    table,
    column,
    value,
    record,
    allTables,
    allColumns,
    studioConfig,
    profileId,
    modelKey,
    processVariables,
    readOnly,
    onChange,
    onValidityChange,
}: StudioCodeEditorProps) => {
    const { t } = useTranslation();
    const { activeTheme } = usePlatformTheme();
    const [monacoInstance, setMonacoInstance] = useState<typeof monacoType | null>(null);
    const profileRecordFingerprint = useMemo(
        () => recordFingerprintForProfile(record, column),
        [column?.name, record]
    );
    const profileRecord = useMemo(() => {
        try {
            return JSON.parse(profileRecordFingerprint);
        } catch (_error) {
            return {};
        }
    }, [profileRecordFingerprint]);

    const metadataProfile = useMemo(() => {
        if (!table || !column) {
            return null;
        }

        return buildMetadataProfile({
            table,
            column,
            record: profileRecord,
            allTables,
            allColumns,
            studioConfig,
        });
    }, [allColumns, allTables, column, profileRecord, studioConfig, table]);

    const builtInProfile = useMemo(() => {
        if (!profileId && (!table || !column)) {
            return null;
        }

        return resolveCodeEditorProfile({
            table,
            column,
            profileId,
            values: profileRecord,
            allTables,
            allColumns,
        });
    }, [allColumns, allTables, column, profileId, profileRecord, table]);

    const processVariablesFingerprint = useMemo(() => JSON.stringify(processVariables), [processVariables]);
    const profile = useMemo(() => {
        const merged = mergeProfiles(metadataProfile, builtInProfile);
        return merged && processVariablesFingerprint !== undefined
            ? { ...merged, processVariables: JSON.parse(processVariablesFingerprint) }
            : merged;
    }, [builtInProfile, metadataProfile, processVariablesFingerprint]);
    const language = profile?.language || languageFromSubtype(column?.column_subtype);
    const path = modelKey
        ? `file:///primum/platform-studio/fluss/${modelKey}.${extensionFromLanguage(language)}`
        : `file:///primum/platform-studio/${table?.name || "fluss"}/${column?.name || profileId || "field"}.${extensionFromLanguage(language)}`;

    const beforeMount: BeforeMount = useCallback((monaco) => {
        applyMonacoPlatformTheme(monaco, activeTheme);
        configureLanguageDiagnostics(monaco, language);
    }, [activeTheme, language]);

    const onMount: OnMount = useCallback((_editor, monaco) => {
        configureLanguageDiagnostics(monaco, language);
        setMonacoInstance(monaco);
    }, [language]);

    useEffect(() => {
        if (!monacoInstance) {
            return;
        }

        applyMonacoPlatformTheme(monacoInstance, activeTheme);
    }, [activeTheme, monacoInstance]);

    useEffect(() => {
        if (!monacoInstance) {
            return undefined;
        }

        configureLanguageDiagnostics(monacoInstance, language);

        const disposable = configureCodeEditor(monacoInstance, profile, path, (key, fallback) =>
            t(key, { defaultValue: fallback || "" })
        );

        return () => disposable.dispose();
    }, [language, monacoInstance, path, profile, t]);

    return (
        <Editor
            height="100%"
            language={language}
            path={path}
            beforeMount={beforeMount}
            onMount={onMount}
            theme="primum-platform-editor"
            value={value}
            onChange={(nextValue) => onChange(nextValue ?? "")}
            onValidate={(markers) => {
                const relevantErrors = markers
                    .filter((marker) => marker.severity >= 4)
                    .sort((first, second) => second.severity - first.severity);

                onValidityChange?.(relevantErrors[0]?.message || null);
            }}
            options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineHeight: 21,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                wordWrap: "on",
                readOnly,
                tabSize: 2,
                renderLineHighlight: "all",
                padding: { top: 12, bottom: 12 },
                quickSuggestions: true,
                suggestOnTriggerCharacters: true,
            }}
        />
    );
};

export default StudioCodeEditor;
